import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { PageCallback } from "react-pdf/dist/cjs/shared/types";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import styled from "styled-components";
import { SelectNameContext } from "../../../context/CheckboxName";
import { EmailSelectedContext } from "../../../context/EmailSelectedContext";
import { isDragContext } from "../../../context/IsDragContext";
import { SignTypeContext } from "../../../context/SignTypeContext";
import { SignIcon } from "../../../svg/SignIcon";
import {
  documentHeight,
  textReactHeight,
  signReactWidth,
  signReactHeight,
} from "../../../utils/measuresForResponsiveness";
import LoadingSpinner from "../../LoadingSpinner";
import { Resizable } from "../Resize";
import { LazyImage } from "./LazyImage";
import {
  Container,
  ContainerSvg,
  DocumentWrapper,
  LoadingText,
  Rect,
  TopWrapper,
  TopWrapperP,
  TopWrapperSpan,
  Wrapper,
} from "./styled";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const StyledPage = styled(Page)`
  display: none;
  canvas {
    position: relative;
    display: block;
    margin: 0 auto;
    max-width: 100%;
    border: 1px solid #ddd;
  }
`;

interface PdfViewerProps {
  pdfUrl: string;
  pdfName: string;
}

interface SignProps {
  x: string;
  y: string;
  y_relative: string;
  page: number;
  type: string;
  color: string;
  icon: string;
  email: string;
  selectName?: string;
}

let global: any = [];
let globalEvent: any = null;

const CustomPDFViewer: React.FC<PdfViewerProps> = ({ pdfUrl, pdfName }) => {
  const rectRef = useRef<any>(null);
  const canvasRefs = useRef<HTMLCanvasElement[]>([]);
  const [numPages, setNumPages] = React.useState(0);
  const [hidden, setHidden] = useState(true);
  const [base64Pdf, setBase64Pdf] = useState<string[]>([]);
  const [signs, setSigns] = useState<SignProps[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedSign, setSelectedSign] = useState<SignProps | null>(null);
  const [shouldUpdateSigns, setShouldUpdateSigns] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>("Aguarde...");
  const [hasElements, setHasElements] = useState<Element>();
  const [wrapperWidth, setWrapperWidth] = useState<number>(0);
  const [newWrapperWidth, setNewWrapperWidth] = useState<number>(0);

  const { selectedOption, setSelectedOption } = useContext(SignTypeContext);
  const { selectedEmail } = useContext(EmailSelectedContext);
  const { selectNameCtx } = useContext(SelectNameContext);

  const { isDrag } = useContext(isDragContext);

  const rubricIconRef = useRef<SVGImageElement>(null);
  const signatureIconRef = useRef<SVGImageElement>(null);
  useEffect(() => {
    const wrapper = document.getElementById("svgContainer");

    const handleResize = () => {
      if (wrapper) {
        const width = wrapper.clientWidth;

        if (width !== wrapperWidth) {
          setWrapperWidth(width);
          setNewWrapperWidth(wrapperWidth);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [newWrapperWidth, wrapperWidth]);

  useEffect(() => {
    if (
      !(document.activeElement instanceof HTMLInputElement) &&
      (newWrapperWidth < wrapperWidth || newWrapperWidth > wrapperWidth)
    ) {
      window.location.reload();
    }
  }, [newWrapperWidth]);

  useEffect(() => {
    if (selectedSign) {
      const updatedSigns = signs.filter((sign) => sign !== selectedSign);
      setSigns(updatedSigns);
      setSelectedSign(null);
      setShouldUpdateSigns(true);
    }
    if (shouldUpdateSigns) {
      setShouldUpdateSigns(false);
    }
    const timeoutId = setTimeout(() => {
      setLoadingMessage("Falha ao renderizar o PDF!");
    }, 10000);
    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSign, shouldUpdateSigns]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      const renderedCanvasList = canvasRefs.current;
      setBase64Pdf(renderedCanvasList.map((element) => element.toDataURL()));
      canvasRefs.current = [];
      setNumPages(numPages);
      setSelectedOption("");
      setCurrentPage(1);
      if (globalEvent) {
        clearTimeout(globalEvent);
      }
      global = [];
      globalEvent = null;
    },
    [setSelectedOption]
  );

  const onRenderPageSuccess = useCallback(
    (page: PageCallback) => {
      global[page._pageIndex] = page;
      if (globalEvent) {
        clearTimeout(globalEvent);
      }
      globalEvent = setTimeout(() => {
        onDocumentLoadSuccess({ numPages });
      }, 200);
    },
    [numPages, onDocumentLoadSuccess]
  );

  const renderPages = useMemo(() => {
    return Array.from({ length: numPages }, (_, i) => (
      <StyledPage
        key={`page_${i + 1}`}
        pageNumber={i + 1}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        onRenderSuccess={onRenderPageSuccess}
        canvasRef={(ref: HTMLCanvasElement | null) => {
          if (ref !== null) {
            canvasRefs.current[i] = ref;
          }
        }}
      />
    ));
  }, [numPages, onRenderPageSuccess]);

  const handleMove = (event: any, index: number) => {
    const element = document.querySelectorAll("#svgContainer image")[index];
    if (element) {
      setHasElements(element);
    }
    if (
      selectedOption === "select" &&
      signs.some(
        (sign) => sign.type === "select" && sign.selectName === selectNameCtx
      )
    ) {
      setSelectedOption("");
      return;
    }
    const rect = hasElements?.getBoundingClientRect();
    const height = documentHeight + 4;
    const equalFields = ["signature", "cpf", "cnpj", "date"];
    if (rectRef.current) {
      if (selectedOption) {
        const xPosition =
          Math.min(
            (rect?.right ?? 0) -
              Number(rectRef.current.style.width.replace("px", "")),
            event.clientX
          ) - (rect?.left ?? 0);
        const yPosition =
          Math.min(
            (rect?.bottom ?? 0) -
              Number(rectRef.current.style.height.replace("px", "")),
            event.clientY
          ) -
          ((rect?.top ?? 0) - index * height);

        rectRef.current.position = "absolute";
        rectRef.current.style.x = `${xPosition}px`;
        rectRef.current.style.y = `${yPosition}px`;
        rectRef.current.style.width =
          selectedOption === "text" || selectedOption === "select"
            ? `${signReactWidth}px`
            : `${textReactHeight}px`;
        rectRef.current.style.height =
          selectedOption === "text" || selectedOption === "select"
            ? `${textReactHeight}px`
            : `${textReactHeight}px`;
        rectRef.current.style.width =
          equalFields.includes(selectedOption) && `${signReactWidth}px`;
        rectRef.current.style.height =
          equalFields.includes(selectedOption) && `${signReactHeight}px`;
        rectRef.current.style.width = selectedOption === "checkbox" && "25px";
        rectRef.current.style.height = selectedOption === "checkbox" && "25px";

        if (rubricIconRef.current && signatureIconRef.current) {
          rubricIconRef.current.setAttribute("x", `${xPosition}px`);
          rubricIconRef.current.setAttribute("y", `${yPosition}px`);
          signatureIconRef.current.setAttribute("x", `${xPosition}px`);
          signatureIconRef.current.setAttribute("y", `${yPosition}px`);
        }
      }
    }
  };

  const destroyElement = () => {
    setHidden(true);
  };

  const onMouseEnter = (event: any, index: number) => {
    setHidden(false);
    handleMove(event, index);
  };
  
  const handleClick = () => {
    
    if (selectedOption) {
      if (rectRef.current) {
        const iconMap: { [key: string]: string } = {
          signature: "assinatura.svg",
          initial: "rubrica.svg",
        };
        const icon = iconMap[selectedOption] || "";

        if (
          selectedOption === "select" &&
          signs.some(
            (sign) =>
              sign.type === "select" && sign.selectName === selectNameCtx
          )
        ) {
          setSelectedOption("");
          return;
        }

        const gapHeight = 4;
        const yPosition = parseFloat(rectRef.current.style.y.replace("px", ""));
        const pageNumber =
          Math.floor(yPosition / (documentHeight + gapHeight)) + 1;

        let y_relative = 0;
        const pageHeight = documentHeight;
        const heightPage =
          pageNumber > 1 ? (pageNumber - 1) * (pageHeight + gapHeight) : 0;
        y_relative = yPosition - heightPage;

        const newSign: SignProps = {
          x: rectRef.current.style.x,
          y: rectRef.current.style.y,
          y_relative: y_relative.toString(),
          page: pageNumber,
          type: selectedOption,
          color: selectedEmail.color,
          icon: icon,
          email: selectedEmail.email,
          selectName: selectNameCtx,
        };
        setSigns([...signs, newSign]);
        if (selectedOption === "text") {
          setSelectedOption("");
        }
      }
    }
  };

  const handleDeleteClick = (sign: SignProps) => {
    setSelectedSign(sign);
  };

  const renderPdfImages = useMemo(() => {
    if (base64Pdf.length === numPages) {
      return base64Pdf.map((element: string, index: number) => (
        <LazyImage
          key={index}
          destroyElement={destroyElement}
          src={element}
          index={index}
          handleMove={handleMove}
          onMouseEnter={onMouseEnter}
        />
      ));
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base64Pdf, numPages, onMouseEnter]);

  const renderNoData = () => {
    return <LoadingText>{loadingMessage}</LoadingText>;
  };

  const renderLoader = () => {
    return <LoadingSpinner />;
  };
  return (
    <>
      <Container
        isDrag={isDrag}
        onScroll={(event: any) => {
          const position = (event.target as Element).scrollTop;
          const currentPage = Math.floor(position / documentHeight) + 1;
          setCurrentPage(currentPage);
        }}
      >
        <TopWrapper>
          <TopWrapperSpan>
            <TopWrapperP>Documento:</TopWrapperP>
            {pdfName}
          </TopWrapperSpan>
          <TopWrapperSpan>
            <TopWrapperP>Página: </TopWrapperP>
            {currentPage} de {numPages}
          </TopWrapperSpan>
        </TopWrapper>
        <DocumentWrapper>
          <Document
            className="documentRef"
            file={pdfUrl}
            noData={renderNoData}
            loading={renderLoader}
            onLoadSuccess={onDocumentLoadSuccess}
            renderMode="canvas"
          >
            {base64Pdf.length === 0 && renderPages}
          </Document>
        </DocumentWrapper>
        <ContainerSvg>
          {base64Pdf.length !== numPages ? (
            <LoadingSpinner />
          ) : (
            <Wrapper
              onClick={handleClick}
              numPages={numPages}
              id="svgContainer"
            >
              {renderPdfImages}

              {signs &&
                signs.map((element, _) => (
                  <Resizable
                    id={`sign-Element-0${_ + 1}`}
                    key={`sign-Element-${
                      element.x.replaceAll(".", "").replace("px", "") +
                      element.y.replaceAll(".", "").replace("px", "")
                    }`}
                    y={element.y}
                    x={element.x}
                    yRelative={element.y_relative}
                    pageNumber={element.page}
                    color={element.color}
                    type={element.type}
                    linkSvg={element.icon}
                    email={element.email}
                    onDelete={() => handleDeleteClick(element)}
                    selectName={element.selectName}
                  />
                ))}
              {!hidden &&
                (selectedOption !== "text" ? (
                  <Rect
                    pointerEvents="none"
                    ref={rectRef}
                    color={selectedEmail.color}
                  >
                    <SignIcon />
                  </Rect>
                ) : (
                  <Rect
                    pointerEvents="none"
                    ref={rectRef}
                    color={selectedEmail.color}
                  ></Rect>
                ))}
            </Wrapper>
          )}
        </ContainerSvg>
      </Container>
    </>
  );
};

export default CustomPDFViewer;
